import { EditorState, PluginKey } from 'prosemirror-state'
import { Decoration } from 'prosemirror-view'

import { getPageLayout, PageLayout } from '#util/PageLayout'
import { extractBlockInfo } from '#util/pagination/helpers'
import { Unit } from '#util/Unit'

import { getConfigData } from '../configData'

// This is a heavy asterisk (U+2731)
export const ASTERISK_MARK = '✱'

export const CHANGED_CSS_CLASS = 'o-script-content__revised'
export const REMOVED_CSS_CLASS = 'o-script-content__removed'

export const GUTTER_RIGHT_CLASS = 'revision_gutter-right'
export const GUTTER_LEFT_CLASS = 'revision_gutter-left'
export const GUTTER_MARK_CLASS = 'revision_line-change-indicator'
export const GUTTER_MARK_REVISED_CLASS = 'revision-gutter-mark--revised'
export const GUTTER_MARK_REMOVED_CLASS = 'revision-gutter-mark--removed'

export function getBlockLayoutData(editorState: EditorState) {
  const { currentBlockFormats, currentPageConfig } = getConfigData(editorState)
  const { pageBlocks } = extractBlockInfo({
    doc: editorState.doc,
    blockFormats: currentBlockFormats,
  })
  const pageLayout = getPageLayout(currentPageConfig)

  return { pageBlocks, pageLayout }
}

export type GutterMark = {
  character: string
  className?: string
}

export type GutterBlockMark = {
  containerPos: number
  key?: string
  marks: GutterMark[]
  side: 'left' | 'right'
  left: Unit
  right: Unit
  height: number
  pageLayout: PageLayout
}

// dual-dialogue columns are measured as two side-by-side blocks rather
// than a single left/right margin, so they don't have one to report —
// fall back to the page's own margins (0 offset from them) in that case
export function resolveGutterMargins(blockInfo: {
  left?: Unit
  right?: Unit
}): { left: Unit; right: Unit } {
  return {
    left: blockInfo.left ?? new Unit(0, 'in'),
    right: blockInfo.right ?? new Unit(0, 'in'),
  }
}

/**
 * Create a gutter for each text block inside the block HTML element to show
 * the marks.
 *
 * NOTE: the widget is inserted at the start of the text block, inserting at
 * the end interferes with the block suggestion popup.
 *
 * When cssModuleStyles is provided (e.g. for the live editor), both the
 * CSS module class and the plain class are added — the plain class is
 * used to apply styles during printing.
 */
export function buildGutterDecorations(
  blocks: GutterBlockMark[],
  cssModuleStyles?: Record<string, string>,
): Decoration[] {
  return blocks.map(
    ({ containerPos, key, marks, side, left, right, height, pageLayout }) => {
      const renderKey = [
        key ?? `${side}:${containerPos}`,
        side,
        height,
        left.toPx().value,
        right.toPx().value,
        pageLayout.pageWidthPx,
        pageLayout.pageMarginsPx.left,
        pageLayout.lineHeightPx,
        marks.map(
          ({ character, className }) => `${character}:${className ?? ''}`,
        ),
      ].join('|')

      return Decoration.widget(
        containerPos,
        () => {
          const container = document.createElement('span')

          // Keep each widget as tall as the block lines in that page.
          container.style.height = height * pageLayout.lineHeightPx + 'px'

          if (side === 'right') {
            // Calculate position to place marks in the right margin
            const gutterOffsetPx =
              pageLayout.pageWidthPx -
              pageLayout.pageMarginsPx.left -
              (right.toPx().value ?? 0) -
              50 // 50px is an arbitrary offset to position the marks from right of the page

            if (cssModuleStyles) {
              container.className = cssModuleStyles[GUTTER_RIGHT_CLASS]
            }
            // this class is used to apply styles during printing
            container.classList.add(GUTTER_RIGHT_CLASS)
            container.style.right = -gutterOffsetPx + 'px'
          } else {
            // Calculate position to place marks in the left margin
            const blockLeftMargin = left.toPx().value ?? 0
            if (cssModuleStyles) {
              container.className = cssModuleStyles[GUTTER_LEFT_CLASS]
            }
            // this class is used to apply styles during printing
            container.classList.add(GUTTER_LEFT_CLASS)
            container.style.left = -blockLeftMargin + 'px'
          }

          // append revision mark characters into the container
          marks.forEach(({ character, className }) => {
            const el = document.createElement('span')
            if (cssModuleStyles) {
              el.className = cssModuleStyles[GUTTER_MARK_CLASS]
            }
            // this class is used to apply styles during printing
            el.classList.add(GUTTER_MARK_CLASS)
            if (className) {
              el.classList.add(className)
            }
            el.textContent = character
            container.append(el)
          })

          return container
        },
        { side: 2, relaxedSide: true, key: renderKey },
      )
    },
  )
}

export type RemovedPositionDetails = {
  position: number
  text: string
}

export type RevisionPluginState = {
  changedRanges: [number, number][]
  removedPositions: RemovedPositionDetails[]
  identical: boolean
}

// Once attached to the DOM we compute where to put revision asterisks.
// This is the data type we use for the map that tracks them
export type AsteriskPositionData = {
  [top: number]: {
    type: 'changed' | 'removed'
    blockId: string
    blockYOffset: number
  }
}

export const PLUGIN_KEY = new PluginKey<RevisionPluginState>(
  'REVISION_COMPARE_PLUGIN',
)

export const buildChangedDecoration = ([from, to]: [
  from: number,
  to: number,
]) =>
  Decoration.inline(from, to, {
    class: CHANGED_CSS_CLASS,
  })

export const buildRemovedDecoration = (
  position: number,
  text?: string | null,
) =>
  Decoration.widget(
    position,
    function toDOM() {
      const el = document.createElement('span')
      el.className = REMOVED_CSS_CLASS
      if (text) el.textContent = text
      return el
    },
    { side: 1 },
  )
